import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { CreatedInvitationResponse, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import {
  emptyParentInvitationForm,
  parentInvitationBody,
  type ParentInvitationForm,
} from './childRequests';
import { createdInvitationNotice, type CreatedInvitationNotice } from './invitations';
import { InvitationLinkNotice } from './InvitationLinkNotice';

export interface InviteParentPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

/**
 * Invites a parent by e-mail before any child is linked. The parent signs in, then asks for their
 * children; the guardian role alone shows no child until a secretary approves a link.
 */
export function InviteParentPanel({
  locale,
  organizationId,
  csrfToken,
}: InviteParentPanelProps): React.JSX.Element {
  const [form, setForm] = useState<ParentInvitationForm>(emptyParentInvitationForm);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [createdLink, setCreatedLink] = useState<CreatedInvitationNotice | null>(null);
  const body = parentInvitationBody(form);

  async function invite(): Promise<void> {
    if (body === null) return;
    setErrorKey(null);
    setCreatedLink(null);
    try {
      const created = await apiRequest<CreatedInvitationResponse>(
        `/api/v1/organizations/${organizationId}/invitations`,
        { method: 'POST', body, csrfToken },
      );
      setCreatedLink(createdInvitationNotice(window.location.origin, created));
      setForm(emptyParentInvitationForm);
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box component="section" aria-labelledby="invite-parent-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="invite-parent-title" gutterBottom>
        {translate(locale, 'inviteParent.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'inviteParent.intro')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {createdLink !== null && <InvitationLinkNotice locale={locale} notice={createdLink} />}
      <Stack
        component="form"
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        onSubmit={(event) => {
          event.preventDefault();
          void invite();
        }}
      >
        <TextField
          required
          size="small"
          type="email"
          label={translate(locale, 'invitations.create.email')}
          value={form.email}
          onChange={(event) => {
            setForm({ ...form, email: event.target.value });
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          required
          size="small"
          label={translate(locale, 'athletes.givenName')}
          value={form.givenName}
          onChange={(event) => {
            setForm({ ...form, givenName: event.target.value });
          }}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <TextField
          required
          size="small"
          label={translate(locale, 'athletes.familyName')}
          value={form.familyName}
          onChange={(event) => {
            setForm({ ...form, familyName: event.target.value });
          }}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
        <Box>
          <Button type="submit" variant="contained" disabled={body === null}>
            {translate(locale, 'inviteParent.submit')}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
